import { create } from 'zustand';
import { useEffect } from 'react';
import { sessionService, type UserRole } from '../services/session.service';
import { keycloakService, AuthError } from '../services/keycloak.service';
import { ALL_MERCHANT_SERVICES, type MerchantService } from '@shared/types/merchant';

const ENABLED_SERVICES_KEY = 'merchant-enabled-services';

interface User {
  email: string;
  name?: string;
}

/** Tracks a login that passed the password step but still needs a TOTP code. */
interface PendingOtp {
  challengeId: string;
  email: string;
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  error: string | null;
  role: UserRole;
  /** Non-null when the current login is awaiting a TOTP code. */
  pendingOtp: PendingOtp | null;
  /** The ID of the currently authenticated merchant (only meaningful when role === 'merchant') */
  merchantId: string | null;
  /** Services enabled for the current merchant (only meaningful when role === 'merchant') */
  enabledServices: MerchantService[];
}

/** Fields collected by the registration form. */
export interface RegisterProfile {
  firstName: string;
  lastName: string;
  country: string;
  email: string;
  password: string;
  acceptedTerms: boolean;
}

interface AuthActions {
  login: (email: string, password: string) => Promise<void>;
  register: (profile: RegisterProfile) => Promise<void>;
  verifyOtp: (code: string) => Promise<void>;
  cancelOtp: () => void;
  socialLogin: (provider: 'google' | 'passkey') => Promise<void>;
  logout: () => void;
  checkSession: () => void;
  clearError: () => void;
  setRole: (role: UserRole) => void;
  setEnabledServices: (services: MerchantService[]) => void;
}

type AuthStore = AuthState & AuthActions;

/**
 * Load the merchant's enabled services from localStorage.
 * When nothing has been explicitly configured yet, default to ALL services
 * so a freshly-logged-in merchant sees the full navigation (Engagement, Sonar,
 * POS, Ecommerce, Billing, etc.) instead of an empty, over-filtered menu.
 */
function loadEnabledServices(): MerchantService[] {
  try {
    const raw = localStorage.getItem(ENABLED_SERVICES_KEY);
    if (!raw) return [...ALL_MERCHANT_SERVICES];
    const parsed = JSON.parse(raw) as MerchantService[];
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : [...ALL_MERCHANT_SERVICES];
  } catch {
    return [...ALL_MERCHANT_SERVICES];
  }
}

/** Persist the authenticated session and flip the store into its logged-in state. */
function finalizeSession(
  set: (partial: Partial<AuthStore>) => void,
  email: string,
  name?: string
): void {
  const resolvedName = name ?? email.split('@')[0];
  sessionService.saveSession(email, resolvedName);
  set({
    isAuthenticated: true,
    user: { email, name: resolvedName },
    isLoading: false,
    error: null,
    pendingOtp: null,
    role: sessionService.getRole(),
    merchantId: sessionService.getMerchantId(),
    enabledServices: loadEnabledServices(),
  });
}

/** Prefer the friendly AuthError message; fall back for unexpected errors. */
function toAuthMessage(error: unknown, fallback: string): string {
  if (error instanceof AuthError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export const useAuthStore = create<AuthStore>((set) => ({
  // Initial state
  isAuthenticated: false,
  user: null,
  isLoading: true,
  error: null,
  role: sessionService.getRole(),
  pendingOtp: null,
  merchantId: sessionService.getMerchantId(),
  enabledServices: loadEnabledServices(),

  // Actions
  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });

    try {
      const result = await keycloakService.login({ email, password });

      // TOTP-enabled account: hold for the OTP challenge, no session yet.
      if (result.otpRequired) {
        set({
          isLoading: false,
          pendingOtp: { challengeId: result.otpChallengeId ?? '', email },
        });
        return;
      }

      finalizeSession(set, result.user?.email ?? email, result.user?.name);
    } catch (error) {
      const errorMessage = toAuthMessage(error, 'Login failed. Please try again.');
      set({ isLoading: false, error: errorMessage });
      throw new Error(errorMessage);
    }
  },

  register: async (profile: RegisterProfile) => {
    set({ isLoading: true, error: null });

    const fallbackName = `${profile.firstName} ${profile.lastName}`.trim();
    try {
      const result = await keycloakService.register({
        firstName: profile.firstName,
        lastName: profile.lastName,
        country: profile.country,
        email: profile.email,
        password: profile.password,
        acceptedTerms: profile.acceptedTerms,
      });
      finalizeSession(set, result.user?.email ?? profile.email, result.user?.name ?? fallbackName);
    } catch (error) {
      const errorMessage = toAuthMessage(error, 'Registration failed. Please try again.');
      set({ isLoading: false, error: errorMessage });
      throw new Error(errorMessage);
    }
  },

  verifyOtp: async (code: string) => {
    const pending = useAuthStore.getState().pendingOtp;
    if (!pending) {
      throw new Error('No pending verification.');
    }
    set({ isLoading: true, error: null });

    try {
      const user = await keycloakService.verifyOtp(pending.challengeId, code, pending.email);
      set({ pendingOtp: null });
      finalizeSession(set, user.email, user.name);
    } catch (error) {
      const errorMessage = toAuthMessage(error, 'Verification failed. Please try again.');
      set({ isLoading: false, error: errorMessage });
      throw new Error(errorMessage);
    }
  },

  cancelOtp: () => set({ pendingOtp: null, error: null }),

  socialLogin: async (provider: 'google' | 'passkey') => {
    set({ isLoading: true, error: null });

    // Live: hand off to the BFF which brokers the IdP redirect. The app resumes
    // via checkSession() on return, so we never resolve locally.
    if (keycloakService.isLive) {
      keycloakService.startSocial(provider);
      return;
    }

    try {
      const user = await keycloakService.socialMock(provider);
      finalizeSession(set, user.email, user.name);
    } catch (error) {
      const errorMessage = toAuthMessage(error, 'Sign-in failed. Please try again.');
      set({ isLoading: false, error: errorMessage });
      throw new Error(errorMessage);
    }
  },

  logout: () => {
    sessionService.logout();
    set({ 
      isAuthenticated: false, 
      user: null, 
      error: null 
    });
    window.location.href = '/login';
  },

  checkSession: () => {
    const session = sessionService.getSession();
    
    if (session && session.isAuthenticated) {
      const user: User = {
        email: session.email,
        name: session.name ?? session.email.split('@')[0],
      };
      set({
        isAuthenticated: true,
        user,
        isLoading: false,
        error: null,
        role: session.role ?? sessionService.getRole(),
        merchantId: sessionService.getMerchantId(),
        enabledServices: loadEnabledServices(),
      });
    } else {
      set({ 
        isAuthenticated: false, 
        user: null,
        isLoading: false,
      });
    }
  },

  clearError: () => {
    set({ error: null });
  },

  setRole: (role: UserRole) => {
    sessionService.setRole(role);
    set({ role, merchantId: sessionService.getMerchantId() });
  },

  setEnabledServices: (services: MerchantService[]) => {
    localStorage.setItem(ENABLED_SERVICES_KEY, JSON.stringify(services));
    set({ enabledServices: services });
  },
}));

/**
 * Hook that wires up automatic logout when the API signals a 401 Unauthorized.
 * Attach this once at the top of the component tree (e.g. App.tsx or AuthProvider).
 */
export function useUnauthorizedHandler() {
  const logout = useAuthStore((s) => s.logout);

  useEffect(() => {
    const handleUnauthorized = () => logout();
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [logout]);
}
